import { Resources } from 'constants/resources';

import CloseIcon from '@mui/icons-material/Close';
import { deleteGatewayRules } from 'config/requests/gatewayRules';
import { useGoBack } from 'hooks/useGoBack';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { Button, DeleteButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { IconButton } from 'shared/mui/IconButtons';
import { getId } from 'utils';

type Props = {
  open: boolean;
  onClose: () => void;
  merchantName?: string;
  id?: string;
};

export const GatewayRuleModalDelete: React.FC<Props> = ({ id, merchantName, open, onClose }) => {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const { goBack } = useGoBack();

  const deleteRule = async () => {
    try {
      await deleteGatewayRules(id);
      notify(translate('app.gatewayRules.ruleDeleted'), { type: 'success' });
      goBack();
    } catch (error) {
      notify(`${translate('app.common.error')}: ${error as string}`, { type: 'error' });
    }
  };

  const onConfirm = async () => {
    await deleteRule();
    await queryClient.refetchQueries({ queryKey: [Resources.GatewayRules] });
  };

  return (
    <Dialog onClose={onClose} open={open}>
      <Box
        sx={{
          display: 'flex',
          top: '50%',
          flexDirection: 'column',
          padding: 0,
          minWidth: '600px',
        }}
      >
        <Card>
          <CardHeader
            appendActions={[
              <IconButton aria-label="close" key="close-button" onClick={onClose}>
                <CloseIcon />
              </IconButton>,
            ]}
            title={translate('app.gatewayRules.deleteRule')}
            titleSize="smallBold"
          />
          <CardContent>
            {translate('app.gatewayRules.ruleDeleteWarning', {
              id: getId(id),
              merchantName,
            })}
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel-button"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <DeleteButton key="delete-button" onClick={onConfirm} />,
            ]}
          />
        </Card>
      </Box>
    </Dialog>
  );
};
